import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const categories = [
  { name: 'Industrial Belts', filter: 'industrial-belts-power-transmission' },
  { name: 'Packaging Machinery', filter: 'packaging-machinery-spare-parts' },
  { name: 'Drives & Motion', filter: 'industrial-drives-motion-systems' },
  { name: 'Electrical Components', filter: 'electrical-components-accessories' },
  { name: 'Food Molds & Dies', filter: 'food-processing-molds-dies' },
  { name: 'Food Ingredients', filter: 'food-processing-ingredients-commodities' },
  { name: 'Fasteners & Hardware', filter: 'industrial-fasteners-hardware' },
];

/* ── Animated SVG illustrations — gold only ── */

const BeltAnim = () => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none">
    <circle cx="10" cy="24" r="7" stroke="#D4AF37" strokeWidth="2" fill="#D4AF3722" />
    <circle cx="38" cy="24" r="7" stroke="#D4AF37" strokeWidth="2" fill="#D4AF3722" />
    <line x1="10" y1="17" x2="38" y2="17" stroke="#D4AF37" strokeWidth="2.5" />
    <line x1="10" y1="31" x2="38" y2="31" stroke="#D4AF37" strokeWidth="2.5" />
    <g>
      <animateTransform attributeName="transform" type="rotate" from="0 10 24" to="360 10 24" dur="2s" repeatCount="indefinite" />
      <line x1="10" y1="17" x2="10" y2="31" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.7" />
      <line x1="3" y1="24" x2="17" y2="24" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.7" />
    </g>
    <g>
      <animateTransform attributeName="transform" type="rotate" from="0 38 24" to="360 38 24" dur="2s" repeatCount="indefinite" />
      <line x1="38" y1="17" x2="38" y2="31" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.7" />
      <line x1="31" y1="24" x2="45" y2="24" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.7" />
    </g>
    <rect x="14" y="15.5" width="5" height="3" rx="1" fill="#D4AF37" opacity="0.7">
      <animateTransform attributeName="transform" type="translate" values="0,0;24,0;24,0" dur="1.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.7;0.7;0" dur="1.5s" repeatCount="indefinite" />
    </rect>
    <rect x="14" y="29.5" width="5" height="3" rx="1" fill="#D4AF37" opacity="0.7">
      <animateTransform attributeName="transform" type="translate" values="24,0;0,0;0,0" dur="1.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.7;0.7;0" dur="1.5s" repeatCount="indefinite" />
    </rect>
  </svg>
);

const PackagingAnim = () => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none">
    <rect x="10" y="20" width="28" height="20" rx="2" stroke="#D4AF37" strokeWidth="2" fill="#D4AF3722" />
    <path d="M10 20 L24 14 L38 20" stroke="#D4AF37" strokeWidth="2" fill="none">
      <animate attributeName="d" values="M10 20 L24 14 L38 20;M10 20 L24 20 L38 20;M10 20 L24 14 L38 20" dur="2s" repeatCount="indefinite" />
    </path>
    <line x1="10" y1="30" x2="38" y2="30" stroke="#D4AF37" strokeWidth="1.5" strokeDasharray="4 3">
      <animate attributeName="stroke-dashoffset" values="0;-14" dur="1s" repeatCount="indefinite" />
    </line>
    <circle cx="24" cy="35" r="3" fill="#D4AF37" opacity="0.5">
      <animate attributeName="opacity" values="0.3;0.8;0.3" dur="1.8s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const GearAnim = () => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none">
    <g>
      <animateTransform attributeName="transform" type="rotate" from="0 18 24" to="360 18 24" dur="3s" repeatCount="indefinite" />
      <circle cx="18" cy="24" r="8" stroke="#D4AF37" strokeWidth="2" fill="#D4AF3722" />
      <circle cx="18" cy="24" r="3" fill="#D4AF37" opacity="0.8" />
      {[0,45,90,135,180,225,270,315].map((a, i) => (
        <rect key={i} x="16" y="10" width="4" height="5" rx="1" fill="#D4AF37" transform={`rotate(${a} 18 24)`} />
      ))}
    </g>
    <g>
      <animateTransform attributeName="transform" type="rotate" from="0 33 24" to="-360 33 24" dur="2s" repeatCount="indefinite" />
      <circle cx="33" cy="24" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="#D4AF3722" />
      <circle cx="33" cy="24" r="2" fill="#D4AF37" opacity="0.8" />
      {[0,60,120,180,240,300].map((a, i) => (
        <rect key={i} x="31.5" y="17" width="3" height="4" rx="0.8" fill="#D4AF37" transform={`rotate(${a} 33 24)`} />
      ))}
    </g>
  </svg>
);

const ElectricalAnim = () => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none">
    <path d="M26 8 L18 26 H24 L22 40 L30 22 H24 Z" fill="#D4AF37">
      <animate attributeName="opacity" values="1;0.3;1;1;0.3;1" dur="1.6s" repeatCount="indefinite" />
    </path>
    <path d="M12 14 Q8 20 12 26" stroke="#D4AF37" strokeWidth="1.5" fill="none" strokeLinecap="round">
      <animate attributeName="opacity" values="0;1;0" dur="1.6s" begin="0.2s" repeatCount="indefinite" />
    </path>
    <path d="M36 14 Q40 20 36 26" stroke="#D4AF37" strokeWidth="1.5" fill="none" strokeLinecap="round">
      <animate attributeName="opacity" values="0;1;0" dur="1.6s" begin="0.5s" repeatCount="indefinite" />
    </path>
    <circle cx="10" cy="18" r="1.5" fill="#D4AF37">
      <animate attributeName="opacity" values="0;1;0" dur="0.8s" begin="0.1s" repeatCount="indefinite" />
    </circle>
    <circle cx="38" cy="22" r="1.5" fill="#D4AF37">
      <animate attributeName="opacity" values="0;1;0" dur="0.8s" begin="0.4s" repeatCount="indefinite" />
    </circle>
    <circle cx="14" cy="36" r="1" fill="#D4AF37">
      <animate attributeName="opacity" values="0;1;0" dur="1.1s" begin="0.7s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const MoldAnim = () => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none">
    <rect x="8" y="8" width="32" height="14" rx="2" stroke="#D4AF37" strokeWidth="2" fill="#D4AF3722">
      <animate attributeName="y" values="8;4;8" dur="2s" repeatCount="indefinite" />
    </rect>
    <rect x="8" y="26" width="32" height="14" rx="2" stroke="#D4AF37" strokeWidth="2" fill="#D4AF3722">
      <animate attributeName="y" values="26;30;26" dur="2s" repeatCount="indefinite" />
    </rect>
    <ellipse cx="24" cy="24" rx="8" ry="5" fill="#D4AF37" opacity="0.7">
      <animate attributeName="opacity" values="0;0.7;0.7;0" dur="2s" repeatCount="indefinite" />
    </ellipse>
    <circle cx="16" cy="15" r="2" fill="#D4AF37" opacity="0.5" />
    <circle cx="24" cy="15" r="2" fill="#D4AF37" opacity="0.5" />
    <circle cx="32" cy="15" r="2" fill="#D4AF37" opacity="0.5" />
  </svg>
);

const IngredientsAnim = () => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none">
    <path d="M18 8 L18 22 L10 38 Q10 40 12 40 H36 Q38 40 38 38 L30 22 L30 8 Z" stroke="#D4AF37" strokeWidth="2" fill="#D4AF3722" />
    <line x1="15" y1="8" x2="33" y2="8" stroke="#D4AF37" strokeWidth="2" />
    <circle cx="20" cy="34" r="2" fill="#D4AF37" opacity="0.8">
      <animate attributeName="cy" values="36;24;36" dur="1.8s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.8;0;0.8" dur="1.8s" repeatCount="indefinite" />
    </circle>
    <circle cx="26" cy="30" r="1.5" fill="#D4AF37" opacity="0.7">
      <animate attributeName="cy" values="34;22;34" dur="2.2s" begin="0.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.7;0;0.7" dur="2.2s" begin="0.5s" repeatCount="indefinite" />
    </circle>
    <circle cx="23" cy="36" r="1" fill="#D4AF37" opacity="0.6">
      <animate attributeName="cy" values="38;28;38" dur="1.5s" begin="1s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.6;0;0.6" dur="1.5s" begin="1s" repeatCount="indefinite" />
    </circle>
    <path d="M13 32 Q24 28 35 32 L35 38 Q24 38 13 38 Z" fill="#D4AF37" opacity="0.3" />
  </svg>
);

const FastenerAnim = () => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none">
    <rect x="20" y="14" width="8" height="22" rx="2" stroke="#D4AF37" strokeWidth="1.5" fill="#D4AF3722" />
    <rect x="16" y="10" width="16" height="7" rx="2" stroke="#D4AF37" strokeWidth="1.5" fill="#D4AF37" opacity="0.8" />
    {[18, 22, 26, 30].map((y, i) => (
      <line key={i} x1="20" y1={y} x2="28" y2={y} stroke="#D4AF37" strokeWidth="1" strokeOpacity="0.6" />
    ))}
    <g>
      <animateTransform attributeName="transform" type="rotate" from="0 24 38" to="360 24 38" dur="2.5s" repeatCount="indefinite" />
      <polygon points="24,31 30,34 30,42 24,45 18,42 18,34" stroke="#D4AF37" strokeWidth="1.5" fill="#D4AF3722" />
      <circle cx="24" cy="38" r="3" stroke="#D4AF37" strokeWidth="1.5" fill="#D4AF3722" />
    </g>
  </svg>
);

const animComponents = [
  BeltAnim, PackagingAnim, GearAnim, ElectricalAnim, MoldAnim, IngredientsAnim, FastenerAnim,
];

const WhatWeSourceSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const SVG_SIZE = 500;
  const CX = 250;
  const CY = 250;
  const ORBIT_R = 178;

  // Icon node size in px (rendered size of the square container)
  const NODE_PX = 56; // w-14 = 56px

  const positions = categories.map((_, i) => {
    const angle = (i / categories.length) * 2 * Math.PI - Math.PI / 2;
    return {
      x: CX + Math.cos(angle) * ORBIT_R,
      y: CY + Math.sin(angle) * ORBIT_R,
    };
  });

  return (
    <section ref={ref} className="py-24 sm:py-32 lg:py-36 bg-white relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="px-4 sm:px-0"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-primary mb-5 sm:mb-7 leading-tight">
              What We Source from
              <br />
              <span className="text-gold-accent">India &amp; Beyond</span>
            </h2>

            <p className="text-base sm:text-lg text-navy-primary/70 max-w-xl leading-relaxed mb-8 sm:mb-10">
              From industrial belts and packaging machinery to food processing equipment and electrical components — True Passage Ventures connects global buyers with verified Indian suppliers across 7 major categories.
            </p>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-3">
              {categories.map((cat, index) => {
                const AnimComp = animComponents[index];
                return (
                  <motion.div
                    key={cat.name}
                    initial={{ opacity: 0, y: 15 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.07 }}
                  >
                    <Link
                      to={`/products?filter=${cat.filter}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-navy-primary/15 rounded-full text-sm font-medium text-navy-primary hover:border-gold-accent hover:text-gold-accent transition-all cursor-pointer shadow-sm hover:shadow-md whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <AnimComp />
                      </span>
                      {cat.name}
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.9 }}
              className="mt-8"
            >
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-7 py-3 bg-navy-primary text-white rounded-full font-semibold text-sm hover:bg-gold-accent hover:text-navy-primary transition-all shadow-md whitespace-nowrap cursor-pointer"
              >
                View All Products
                <i className="ri-arrow-right-line"></i>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right: Radial visual */}
          <motion.div
            className="relative flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="w-full max-w-[520px]" style={{ aspectRatio: '1 / 1', position: 'relative' }}>

              {/* SVG layer: spokes + pulse dots only (no orbit rings) */}
              <svg
                viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                width="100%"
                height="100%"
                style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
              >
                <defs>
                  <filter id="glow2" x="-80%" y="-80%" width="260%" height="260%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <radialGradient id="hubGlow2" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Hub glow */}
                <circle cx={CX} cy={CY} r="100" fill="url(#hubGlow2)" />

                {/* Spoke lines — connect hub to icon centre */}
                {positions.map((pos, i) => (
                  <line
                    key={`spoke-${i}`}
                    x1={CX} y1={CY}
                    x2={pos.x} y2={pos.y}
                    stroke="#D4AF37"
                    strokeWidth="1.2"
                    strokeDasharray="5 4"
                    strokeOpacity={activeIndex === i ? 0.65 : 0.22}
                    style={{ transition: 'stroke-opacity 0.3s' }}
                  />
                ))}

                {/* Animated pulse dots travelling along spokes */}
                {positions.map((pos, i) => (
                  <circle key={`pulse-${i}`} r="5" fill="#D4AF37" filter="url(#glow2)" opacity="0.8">
                    <animateMotion
                      dur={`${2.6 + i * 0.28}s`}
                      repeatCount="indefinite"
                      begin={`${i * 0.42}s`}
                      path={`M ${CX} ${CY} L ${pos.x} ${pos.y}`}
                    />
                    <animate attributeName="opacity" values="0;0.85;0" dur={`${2.6 + i * 0.28}s`} repeatCount="indefinite" begin={`${i * 0.42}s`} />
                    <animate attributeName="r" values="3;5.5;3" dur={`${2.6 + i * 0.28}s`} repeatCount="indefinite" begin={`${i * 0.42}s`} />
                  </circle>
                ))}
              </svg>

              {/* Center Hub */}
              <div
                style={{
                  position: 'absolute',
                  left: `${(CX / SVG_SIZE) * 100}%`,
                  top: `${(CY / SVG_SIZE) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10,
                }}
              >
                <motion.div
                  className="relative"
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <motion.div
                    className="absolute rounded-full bg-gold-accent/20"
                    style={{ width: 110, height: 110, top: -15, left: -15 }}
                    animate={{ scale: [1, 1.5, 1.5], opacity: [0.35, 0, 0] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: 'easeOut' }}
                  />
                  <motion.div
                    className="absolute rounded-full bg-gold-accent/15"
                    style={{ width: 110, height: 110, top: -15, left: -15 }}
                    animate={{ scale: [1, 1.8, 1.8], opacity: [0.25, 0, 0] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: 'easeOut', delay: 1 }}
                  />
                  <div className="w-20 h-20 bg-navy-primary rounded-full flex flex-col items-center justify-center shadow-2xl relative z-10 border-2 border-gold-accent/40">
                    <i className="ri-global-line text-gold-accent text-2xl"></i>
                    <span className="text-white text-[9px] font-semibold mt-0.5 tracking-wide">INDIA</span>
                  </div>
                </motion.div>
              </div>

              {/* Category nodes — icon box centre is anchored exactly on the circle point */}
              {categories.map((cat, index) => {
                const pos = positions[index];
                const AnimComp = animComponents[index];
                const isActive = activeIndex === index;

                // We want the centre of the NODE_PX icon box to sit at (pos.x, pos.y).
                // translate(-50%, -50%) on the outer div already does this for the div itself,
                // but the label below the icon shifts the visual centre downward.
                // We compensate by nudging the whole group up by half the label height (~18px in SVG units).
                // Label height ≈ 18px; container is 520px wide mapping to SVG_SIZE=500 units → scale ≈ 500/520
                // Keep it simple: just use translate(-50%, -50%) on the icon box directly, and let the label flow below.

                return (
                  <div
                    key={cat.name}
                    style={{
                      position: 'absolute',
                      left: `${(pos.x / SVG_SIZE) * 100}%`,
                      top: `${(pos.y / SVG_SIZE) * 100}%`,
                      // Shift up by half the icon height so the icon centre (not the group centre) lands on the circle
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={isInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    >
                      <Link
                        to={`/products?filter=${cat.filter}`}
                        className="flex flex-col items-center gap-1 group cursor-pointer"
                      >
                        <motion.div
                          className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md border transition-all duration-300 ${
                            isActive
                              ? 'bg-gold-accent/10 border-gold-accent shadow-xl scale-110'
                              : 'bg-white border-gold-accent/35 group-hover:border-gold-accent group-hover:shadow-lg'
                          }`}
                          whileHover={{ scale: 1.12 }}
                        >
                          <AnimComp />
                        </motion.div>
                        <span
                          className={`text-[10px] font-semibold text-center leading-tight max-w-[72px] transition-colors duration-300 ${
                            isActive ? 'text-gold-accent' : 'text-navy-primary/75 group-hover:text-gold-accent'
                          }`}
                        >
                          {cat.name}
                        </span>
                      </Link>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default WhatWeSourceSection;
